<template>
  <AModal
    :model-value="modelValue"
    size="xl"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <template #title>
      <span class="hpv__title">
        <i class="material-icons-round hpv__title-icon">smartphone</i>
        앱 홈 노출 미리보기
      </span>
    </template>
    <template #sub>
      지금 저장된 진열 순서와 노출 설정을 그대로 그린 화면입니다. 실제 앱 디자인과 세부 여백은 다를 수 있습니다.
    </template>

    <div v-if="section" class="hpv">
      <div class="hpv__meta">
        <APill kind="dark">{{ displayTypeLabel(section.display_type) }}</APill>
        <APill :kind="section.active_yn === 'Y' ? 'ok' : 'err'" dot>
          {{ section.active_yn === 'Y' ? '노출 중' : '미노출' }}
        </APill>
        <span class="hpv__meta-text">
          진열 {{ items.length }}권 중 <b>{{ visibleItems.length }}권</b> 노출
          <template v-if="hiddenCount"> · {{ hiddenCount }}권 대기</template>
        </span>
      </div>

      <!-- 앱 홈 화면 재현 -->
      <div class="hpv__stage">
        <div class="hpv__section-head">
          <h3>
            {{ pickTitle(section, previewLang) }}
            <!-- 커버 그리드만 아래 '더보기'로 넘기고, 나머지는 헤더의 View all 로 넘어갑니다 -->
            <span v-if="section.display_type !== 'more'" class="hpv__viewall">View all</span>
          </h3>
          <div class="hpv__lang">
            <button
              v-for="l in HOME_LANGS"
              :key="l.code"
              type="button"
              :class="['hpv__lang-btn', { 'is-on': previewLang === l.code }]"
              :title="`${l.label} 타이틀로 보기`"
              @click="previewLang = l.code"
            >
              {{ l.code }}
            </button>
          </div>
        </div>

        <div v-if="!visibleItems.length" class="hpv__blank">
          <i class="material-icons-round">inbox</i>
          진열된 도서가 없어 앱에서는 이 섹션이 통째로 빠집니다.
        </div>

        <template v-else>
          <div :class="['hpv__books', `hpv__books--${section.display_type}`]">
            <article v-for="b in visibleItems" :key="b.item_idx || b.barcode" class="hpv__card">
              <div class="hpv__card-cover">
                <img :src="b.cover_image_url || COVER_FALLBACK" :alt="b.title" @error="onCoverError" />
                <span v-if="!isItemPublic(b)" class="hpv__card-flag">미공개</span>
              </div>
              <div class="hpv__card-body">
                <span class="hpv__card-title">{{ b.title }}</span>
                <span class="hpv__card-pub">{{ b.publisher || b.editor_name || '출판사 미등록' }}</span>
                <span class="hpv__card-price">{{ fmtPrice(b.list_price, b.currency_code) }}</span>
                <span class="hpv__card-btn">
                  <i class="material-icons-round">shopping_cart</i>
                  구매 · 상세
                </span>
              </div>
            </article>
          </div>

          <div v-if="section.display_type === 'more'" class="hpv__more">
            더보기 <b>({{ visibleItems.length }} / {{ items.length }})</b>
          </div>
          <div v-else-if="isScrollType(section.display_type)" class="hpv__scroll-hint">
            <i class="material-icons-round">swipe</i>
            옆으로 밀어서 봅니다
          </div>
        </template>
      </div>

      <div class="hpv__notice">
        <i class="material-icons-round">info</i>
        <span>
          가격은 앱에서 스토어 상품가로 표시됩니다. 여기 금액은 참고용이며,
          교재에는 저자 항목이 없어 <b>출판사</b>(없으면 저작권자)를 대신 보여줍니다.
        </span>
      </div>
    </div>

    <template #foot>
      <ABtn kind="ghost" @click="$emit('update:modelValue', false)">닫기</ABtn>
    </template>
  </AModal>
</template>

<script>
import AModal from "@/components/Pineapple/AModal.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import {
  HOME_LANGS,
  pickTitle,
  displayTypeLabel,
  isScrollType,
  isItemPublic,
  fmtPrice,
  onCoverError,
  COVER_FALLBACK,
} from "@/utils/homeSection";

export default {
  name: "HomeSectionPreviewModal",
  components: { AModal, ABtn, APill },
  props: {
    modelValue: { type: Boolean, default: false },
    section: { type: Object, default: null },
    items: { type: Array, default: () => [] },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      HOME_LANGS,
      COVER_FALLBACK,
      previewLang: "ko",
    };
  },
  computed: {
    // display_limit=0 이면 진열한 전부를 내보냅니다.
    visibleItems() {
      const limit = this.section?.display_limit || 0;
      return limit ? this.items.slice(0, limit) : this.items;
    },
    hiddenCount() {
      return Math.max(0, this.items.length - this.visibleItems.length);
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.previewLang = "ko";
    },
  },
  methods: {
    pickTitle,
    displayTypeLabel,
    isScrollType,
    isItemPublic,
    fmtPrice,
    onCoverError,
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.hpv {
  display: flex;
  flex-direction: column;
  gap: 14px;

  &__title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 800;
    color: $p-ink;
  }

  &__title-icon {
    color: $p-point-deep;
    font-size: 20px !important;
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__meta-text {
    font-size: 11px;
    color: $p-ink-mid;

    b { color: $p-ink; font-weight: 800; }
  }

  // 앱 화면 배경을 흉내 낸 무대
  &__stage {
    padding: 18px;
    background: $p-bg;
    border: 1px solid $p-line;
    border-radius: $p-radius-lg;
  }

  &__section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;

    h3 {
      margin: 0;
      display: flex;
      align-items: baseline;
      gap: 10px;
      font-size: 17px;
      font-weight: 800;
      letter-spacing: -0.02em;
      color: $p-ink;
    }
  }

  &__viewall {
    font-size: 11px;
    font-weight: 700;
    color: $p-ink-soft;
    flex-shrink: 0;
  }

  &__lang {
    display: inline-flex;
    gap: 3px;
  }

  &__lang-btn {
    padding: 3px 8px;
    border: 1px solid $p-line;
    border-radius: 999px;
    background: $p-panel;
    color: $p-ink-soft;
    font-family: $p-font-mono;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease, border-color $p-dur $p-ease;

    &.is-on {
      background: $p-ink;
      border-color: $p-ink;
      color: $p-point;
    }
  }

  &__blank {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 28px 16px;
    justify-content: center;
    color: $p-ink-soft;
    font-size: 12px;

    > i { font-size: 18px !important; }
  }

  &__books {
    display: grid;
    gap: 12px;

    // 세로 리스트: 가로로 넓은 행 카드가 한 열로 쌓입니다.
    &--list {
      grid-template-columns: 1fr;
      gap: 8px;
    }

    // 가로 스크롤: 카드가 한 줄로 이어지고 옆으로 밀어서 봅니다.
    &--carousel {
      grid-auto-flow: column;
      grid-auto-columns: 132px;
      overflow-x: auto;
      padding-bottom: 6px;
    }

    // 가로 2행: 2행으로 쌓인 채 옆으로 밀립니다. 밴드 배경은 앱에서만 깔립니다.
    &--carousel2 {
      grid-auto-flow: column;
      grid-template-rows: repeat(2, minmax(0, 1fr));
      grid-auto-columns: 132px;
      overflow-x: auto;
      padding-bottom: 6px;
    }

    // 커버 그리드: 그리드로 깔고 아래에 더보기 버튼을 답니다.
    &--more {
      grid-template-columns: repeat(auto-fill, minmax(132px, 1fr));
    }
  }

  // 세로 리스트만 카드가 가로로 눕습니다.
  &__books--list &__card {
    flex-direction: row;
    align-items: flex-start;
    gap: 11px;
    padding: 9px 11px;
  }

  &__books--list &__card-cover {
    width: 58px;
    flex-shrink: 0;
  }

  &__books--list &__card-body {
    flex: 1;
  }

  &__card {
    display: flex;
    flex-direction: column;
    gap: 8px;
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    padding: 10px;
    min-width: 0;
  }

  &__card-cover {
    position: relative;
    width: 100%;
    aspect-ratio: 5 / 7;
    border-radius: $p-radius-sm;
    overflow: hidden;
    background: $p-panel-alt;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  }

  &__card-flag {
    position: absolute;
    top: 5px;
    left: 5px;
    padding: 2px 6px;
    border-radius: 999px;
    background: $p-err-soft;
    color: $p-err-ink;
    font-size: 9px;
    font-weight: 800;
  }

  &__card-body {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
  }

  &__card-title {
    font-size: 12px;
    font-weight: 800;
    line-height: 1.35;
    color: $p-ink;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__card-pub {
    font-size: 10px;
    color: $p-ink-soft;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__card-price {
    margin-top: 2px;
    font-family: $p-font-mono;
    font-size: 12px;
    font-weight: 700;
    color: $p-ink;
  }

  &__card-btn {
    margin-top: 5px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 6px 0;
    border-radius: $p-radius-sm;
    background: $p-point;
    color: $p-ink;
    font-size: 11px;
    font-weight: 800;

    > i { font-size: 13px !important; }
  }

  &__more {
    margin-top: 14px;
    padding: 10px;
    text-align: center;
    border: 1px solid $p-line-hard;
    border-radius: $p-radius-md;
    background: $p-panel;
    color: $p-ink-mid;
    font-size: 12px;
    font-weight: 800;

    b { color: $p-ink-soft; font-weight: 700; }
  }

  &__scroll-hint {
    margin-top: 10px;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    color: $p-ink-soft;

    > i { font-size: 14px !important; }
  }

  &__notice {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    padding: 11px 13px;
    background: $p-info-soft;
    border: 1px solid rgba($p-info, 0.28);
    border-radius: $p-radius-md;
    font-size: 12px;
    line-height: 1.55;
    color: $p-ink;

    > i { font-size: 18px !important; color: $p-info; flex-shrink: 0; }
  }
}
</style>
